"use client";

import {
  BROWSER_VIEW_WEBSOCKET_PATH,
  BrowserViewSignal,
  type BrowserViewClientMessage,
  type BrowserViewerInput,
  type BrowserViewFrameHeader,
  type DesktopPreviewFavicon,
  type ScopedThreadRef,
} from "@afar-research/contracts";
import { decodeBrowserViewFrame } from "@afar-research/shared/browserViewFrame";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { useEffect, useRef, useState } from "react";

import { cn } from "~/lib/utils";

import {
  cdpKeyInput,
  cdpModifiers,
  cdpMouseButton,
  pagePointFromView,
  viewPointFromPage,
} from "./streamedBrowserView.logic";
import { authorizeViewSocket, viewSocketRefusals, type ViewSocketRefusals } from "./viewSocket";

const decodeSignal = Schema.decodeUnknownOption(Schema.fromJsonString(BrowserViewSignal));
const RECONNECT_DELAY_MS = 1_000;
/** How long the "someone else is using this browser" note stays after their last input. */
const OTHER_INPUT_NOTICE_MS = 2_500;

let viewerSequence = 0;
const newViewerId = () =>
  `viewer-${Date.now().toString(36)}-${(viewerSequence++).toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

/** Opens the authorized view socket for one tab once the Environment is connected. */
async function openViewSocket(
  threadRef: ScopedThreadRef,
  tabId: string,
  viewerId: string,
  signal: AbortSignal,
  refusals: ViewSocketRefusals,
): Promise<WebSocket | null> {
  const base = await authorizeViewSocket(
    threadRef.environmentId,
    BROWSER_VIEW_WEBSOCKET_PATH,
    signal,
    refusals,
  );
  if (base === null) return null;
  const url = new URL(base);
  url.searchParams.set("threadId", threadRef.threadId);
  url.searchParams.set("tabId", tabId);
  url.searchParams.set("viewerId", viewerId);
  const socket = new WebSocket(url);
  socket.binaryType = "arraybuffer";
  return socket;
}

/**
 * The Collaborative Browser as every client sees it (ADR-0137): frames the
 * Environment's Chromium streams, drawn into a canvas, with this panel's pointer
 * and keyboard forwarded back. The socket is open only while the panel is
 * visible, so a hidden panel costs the Environment nothing, and the canvas
 * repaints only when a frame arrives.
 */
export function StreamedBrowserView(props: {
  readonly threadRef: ScopedThreadRef;
  readonly tabId: string;
  readonly visible: boolean;
  readonly className?: string;
  /** False for a thumbnail that shows the page but does not take input. */
  readonly interactive?: boolean;
  /**
   * False for a view that should not decide the page's size, such as the
   * floating player beside a panel showing the same tab: it scales whatever
   * size the panel chose instead of shrinking the page to itself.
   */
  readonly sizesPage?: boolean;
  /** The canvas the page is drawn into, for capturing what this panel shows. */
  readonly onCanvas?: (canvas: HTMLCanvasElement | null) => void;
  /** Whether the view socket is open, so a capture is not taken of a blank canvas. */
  readonly onConnectedChange?: (connected: boolean) => void;
  /** A mouse's back or forward button over the page, as in any browser window. */
  readonly onNavigate?: (direction: "back" | "forward") => void;
  /** The page's icon, whenever the Environment finds a new one. */
  readonly onFavicon?: (favicon: DesktopPreviewFavicon) => void;
}) {
  const {
    threadRef,
    tabId,
    visible,
    className,
    interactive = true,
    sizesPage = true,
    onCanvas,
    onConnectedChange,
    onNavigate,
    onFavicon,
  } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const frameRef = useRef<BrowserViewFrameHeader | null>(null);
  const [viewerId] = useState(newViewerId);
  // Read through a ref so a new handler does not reopen the socket.
  const onFaviconRef = useRef(onFavicon);
  onFaviconRef.current = onFavicon;
  const [connected, setConnected] = useState(false);
  const [controller, setController] = useState<"agent" | "none">("none");
  const [cursor, setCursor] = useState<{ x: number; y: number; phase: "move" | "click" } | null>(
    null,
  );
  const [otherInputAt, setOtherInputAt] = useState<number | null>(null);
  const [closedReason, setClosedReason] = useState<string | null>(null);
  useEffect(() => onConnectedChange?.(connected), [connected, onConnectedChange]);

  const send = (message: BrowserViewClientMessage) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  };
  const sendInput = (event: BrowserViewerInput) => send({ type: "input", event });

  // One socket per visible panel; reconnects after an unexpected drop.
  useEffect(() => {
    if (!visible) return;
    let disposed = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const refusals = viewSocketRefusals(BROWSER_VIEW_WEBSOCKET_PATH);
    const abort = new AbortController();
    setClosedReason(null);

    const connect = async () => {
      const socket = await openViewSocket(threadRef, tabId, viewerId, abort.signal, refusals).catch(
        () => null,
      );
      if (disposed) {
        socket?.close();
        return;
      }
      if (socket === null) {
        retry = setTimeout(() => void connect(), RECONNECT_DELAY_MS);
        return;
      }
      socketRef.current = socket;
      let opened = false;
      socket.addEventListener("open", () => {
        opened = true;
        refusals.reset();
        setConnected(true);
        const container = containerRef.current;
        if (container && sizesPage) {
          const rect = container.getBoundingClientRect();
          send({
            type: "resize",
            width: Math.max(1, Math.round(rect.width)),
            height: Math.max(1, Math.round(rect.height)),
          });
        }
      });
      socket.addEventListener("message", (message) => {
        if (typeof message.data === "string") {
          const signal = decodeSignal(message.data);
          if (Option.isNone(signal)) return;
          const value = signal.value;
          switch (value.type) {
            case "cursor":
              setCursor({ x: value.x, y: value.y, phase: value.phase });
              return;
            case "controller":
              setController(value.controller);
              if (value.controller === "none") setCursor(null);
              return;
            case "input":
              setOtherInputAt(Date.now());
              return;
            case "favicon":
              onFaviconRef.current?.({
                pageUrl: value.pageUrl,
                dataUrl: value.dataUrl,
                capturedAt: value.capturedAt,
              });
              return;
            case "closed":
              setClosedReason(value.reason);
              socket.close();
              return;
          }
        }
        const frame = decodeBrowserViewFrame(message.data as ArrayBuffer);
        if (frame === null) return;
        setClosedReason(null);
        frameRef.current = frame.header;
        void createImageBitmap(new Blob([frame.jpeg as BlobPart], { type: "image/jpeg" })).then(
          (bitmap) => {
            const canvas = canvasRef.current;
            const container = containerRef.current;
            if (!canvas || !container) {
              bitmap.close();
              return;
            }
            const rect = container.getBoundingClientRect();
            const ratio = window.devicePixelRatio || 1;
            const width = Math.max(1, Math.round(rect.width * ratio));
            const height = Math.max(1, Math.round(rect.height * ratio));
            if (canvas.width !== width || canvas.height !== height) {
              canvas.width = width;
              canvas.height = height;
            }
            const context = canvas.getContext("2d");
            if (context) {
              const scale = Math.min(
                rect.width / frame.header.deviceWidth,
                rect.height / frame.header.deviceHeight,
                1,
              );
              const drawWidth = frame.header.deviceWidth * scale;
              const drawHeight = frame.header.deviceHeight * scale;
              context.clearRect(0, 0, width, height);
              context.drawImage(
                bitmap,
                ((rect.width - drawWidth) / 2) * ratio,
                ((rect.height - drawHeight) / 2) * ratio,
                drawWidth * ratio,
                drawHeight * ratio,
              );
            }
            bitmap.close();
          },
        );
      });
      socket.addEventListener("close", (event) => {
        if (socketRef.current === socket) socketRef.current = null;
        if (!opened && !disposed) {
          refusals.warn("it closed before it opened.", { code: event.code });
        }
        setConnected(false);
        if (!disposed) retry = setTimeout(() => void connect(), RECONNECT_DELAY_MS);
      });
    };
    void connect();

    return () => {
      disposed = true;
      abort.abort();
      if (retry !== undefined) clearTimeout(retry);
      socketRef.current?.close();
      socketRef.current = null;
      setConnected(false);
    };
    // threadRef is identified by its environment and thread ids.
  }, [sizesPage, threadRef.environmentId, threadRef.threadId, tabId, viewerId, visible]);

  // The panel's size is the page's size while the tab fills the panel.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !visible || !sizesPage) return;
    const observer = new ResizeObserver(() => {
      const rect = container.getBoundingClientRect();
      send({
        type: "resize",
        width: Math.max(1, Math.round(rect.width)),
        height: Math.max(1, Math.round(rect.height)),
      });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [sizesPage, visible]);

  // The notice fades on its own; no timer runs while nobody else is typing.
  useEffect(() => {
    if (otherInputAt === null) return;
    const timeout = setTimeout(() => setOtherInputAt(null), OTHER_INPUT_NOTICE_MS);
    return () => clearTimeout(timeout);
  }, [otherInputAt]);

  const pagePoint = (clientX: number, clientY: number) => {
    const container = containerRef.current;
    const frame = frameRef.current;
    if (!container || !frame) return null;
    return pagePointFromView(container.getBoundingClientRect(), frame, clientX, clientY);
  };

  const onPointer =
    (type: "mousePressed" | "mouseReleased" | "mouseMoved") =>
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!interactive) return;
      // Thumb buttons navigate the tab rather than reaching the page.
      if (event.button === 3 || event.button === 4) {
        event.preventDefault();
        if (type === "mouseReleased") onNavigate?.(event.button === 3 ? "back" : "forward");
        return;
      }
      const point = pagePoint(event.clientX, event.clientY);
      if (point === null) return;
      if (type === "mousePressed") canvasRef.current?.focus({ preventScroll: true });
      sendInput({
        kind: "mouse",
        type,
        ...point,
        button:
          type === "mouseMoved" && event.buttons === 0 ? "none" : cdpMouseButton(event.button),
        clickCount: type === "mouseMoved" ? 0 : Math.min(3, Math.max(1, event.detail)),
        modifiers: cdpModifiers(event),
      });
    };

  const cursorPosition =
    cursor && frameRef.current && containerRef.current
      ? viewPointFromPage(
          {
            left: 0,
            top: 0,
            width: containerRef.current.clientWidth,
            height: containerRef.current.clientHeight,
          },
          frameRef.current,
          cursor.x,
          cursor.y,
        )
      : null;

  return (
    <div ref={containerRef} className={cn("relative overflow-hidden bg-background", className)}>
      <canvas
        ref={(canvas) => {
          canvasRef.current = canvas;
          onCanvas?.(canvas);
        }}
        className="absolute inset-0 h-full w-full outline-none"
        tabIndex={interactive ? 0 : -1}
        aria-label="Browser page"
        onPointerDown={onPointer("mousePressed")}
        onPointerUp={onPointer("mouseReleased")}
        onPointerMove={onPointer("mouseMoved")}
        onContextMenu={(event) => event.preventDefault()}
        onWheel={(event) => {
          if (!interactive) return;
          const point = pagePoint(event.clientX, event.clientY);
          if (point === null) return;
          sendInput({
            kind: "wheel",
            ...point,
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            modifiers: cdpModifiers(event),
          });
        }}
        onKeyDown={(event) => {
          if (!interactive) return;
          event.preventDefault();
          sendInput(cdpKeyInput(event, "down"));
        }}
        onKeyUp={(event) => {
          if (!interactive) return;
          event.preventDefault();
          sendInput(cdpKeyInput(event, "up"));
        }}
        onPaste={(event) => {
          if (!interactive) return;
          const text = event.clipboardData.getData("text/plain");
          if (text) sendInput({ kind: "insertText", text });
          event.preventDefault();
        }}
      />
      {cursorPosition && controller === "agent" ? (
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute z-10 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-primary bg-primary/30 transition-[left,top] duration-150",
            cursor?.phase === "click" && "scale-75",
          )}
          style={{ left: cursorPosition.left, top: cursorPosition.top }}
        />
      ) : null}
      {otherInputAt !== null ? (
        <div className="pointer-events-none absolute right-3 top-3 z-10 rounded-full border border-border/70 bg-background/90 px-2.5 py-1 text-[11px] font-medium shadow-sm">
          Someone else is using this browser
        </div>
      ) : null}
      {!connected && visible && closedReason === null ? (
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-xs text-muted-foreground">
          Connecting to the browser…
        </div>
      ) : null}
      {closedReason !== null ? (
        <div className="absolute inset-0 grid place-items-center p-6 text-center text-xs text-muted-foreground">
          {closedReason}
        </div>
      ) : null}
    </div>
  );
}
